// === SAA:UTILS:PROGRESS-FRONTIER ===
// The stepper's forward reach per flow = the furthest position the user has walked to
// through the screens themselves (Volgende), valid for the data as it was then. Every
// arrival re-baselines: frontier = max(min(frontier, first changed step), arrived
// position) and a snapshot of the per-step change fingerprints is taken. A change
// since the snapshot (products toggled, client field edited, a premium recalculated,
// payment edited, an acceptance answer changed) lowers the effective frontier to that
// step, so "Offerte" closes until the flow has been walked through again, and reopens
// when the data is back to exactly what it was ("did not change anything"). Per-step
// fingerprints capture what the USER can change, not engine echo: the product step
// hashes the premium per product, so a remount recalculation with the same result is
// not a change. Session-scoped; cleared with the rest of the SAA session state.
import { stableStringify } from './stableStringify';
import { getProductOrder } from '../workflowConfig';
import { readCalculatedPremium } from './productPremium';
import type { WorkflowReads } from './stepCompleteness';

export type ProgressFlow = 'quote' | 'application';

export const PROGRESS_FRONTIER_KEYS: Record<ProgressFlow, string> = {
  quote: 'progress_frontier_quote',
  application: 'progress_frontier_application',
};

/** Stepper positions per flow, in config order (ids = workflow.progressSteps / applicationProgressSteps). */
export const PROGRESS_FLOW_STEPS: Record<ProgressFlow, readonly string[]> = {
  quote: ['products', 'client-information', 'product-details', 'quote'],
  application: ['payment', 'questions', 'confirm'],
};

const FINGERPRINTS: Record<string, (reads: WorkflowReads) => string> = {
  products: ({ selectedProducts }) =>
    stableStringify(getProductOrder().filter((id) => selectedProducts?.[id])),
  'client-information': ({ getStepData }) => stableStringify(getStepData('client-information')?.clientInfo ?? null),
  'product-details': ({ selectedProducts, getStepData }) =>
    stableStringify(
      getProductOrder()
        .filter((id) => selectedProducts?.[id])
        .map((id) => [id, readCalculatedPremium(id, getStepData(id))]),
    ),
  payment: ({ getStepData, paymentData }) =>
    stableStringify(getStepData('payment-information')?.paymentInfo ?? paymentData?.paymentInfo ?? null),
  questions: ({ getStepData }) => {
    const step = getStepData('acceptance-questions');
    return stableStringify({ answers: step?.answers ?? null, explanations: step?.explanations ?? null });
  },
};

export const progressFingerprints = (flow: ProgressFlow, reads: WorkflowReads): Record<string, string> =>
  Object.fromEntries(PROGRESS_FLOW_STEPS[flow].map((id) => [id, FINGERPRINTS[id]?.(reads) ?? '']));

interface FrontierState {
  frontier: number;
  snapshot: Record<string, string>;
}

const readState = (flow: ProgressFlow): FrontierState => {
  if (typeof sessionStorage === 'undefined') return { frontier: -1, snapshot: {} };
  try {
    const raw = sessionStorage.getItem(PROGRESS_FRONTIER_KEYS[flow]);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed.frontier === 'number' && parsed.snapshot && typeof parsed.snapshot === 'object') return parsed;
  } catch (_) { /* corrupt = no frontier */ }
  return { frontier: -1, snapshot: {} };
};

const writeState = (flow: ProgressFlow, state: FrontierState): void => {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(PROGRESS_FRONTIER_KEYS[flow], JSON.stringify(state));
};

/** Lowest step index whose fingerprint differs from the snapshot; Infinity when nothing changed. */
export const firstChangedStep = (flow: ProgressFlow, reads: WorkflowReads): number => {
  const { snapshot } = readState(flow);
  const now = progressFingerprints(flow, reads);
  const idx = PROGRESS_FLOW_STEPS[flow].findIndex((id) => (snapshot[id] ?? '') !== now[id]);
  return idx === -1 ? Infinity : idx;
};

/** Furthest position a forward click may target right now. */
export const effectiveFrontier = (flow: ProgressFlow, reads: WorkflowReads): number =>
  Math.min(readState(flow).frontier, firstChangedStep(flow, reads));

/** Called on every arrival at a stepper position; re-baselines the frontier and the snapshot. */
export const arriveAtProgressPosition = (flow: ProgressFlow, position: number, reads: WorkflowReads): void => {
  const frontier = Math.max(effectiveFrontier(flow, reads), position);
  writeState(flow, { frontier, snapshot: progressFingerprints(flow, reads) });
};

/** Stepper position for a workflow pathname; null for pages outside both steppers (end page). */
export const progressPositionForPath = (pathname: string): { flow: ProgressFlow; index: number } | null => {
  const page = pathname.replace(/\/+$/, '').split('/').pop() ?? '';
  if (page === 'product-selection') return { flow: 'quote', index: 0 };
  if (page === 'client-information') return { flow: 'quote', index: 1 };
  if ((getProductOrder() as readonly string[]).includes(page)) return { flow: 'quote', index: 2 };
  if (page === 'summary') return { flow: 'quote', index: 3 };
  if (page === 'payment-information') return { flow: 'application', index: 0 };
  if (page === 'acceptance-questions') return { flow: 'application', index: 1 };
  if (page === 'confirmation') return { flow: 'application', index: 2 };
  return null;
};
